<template>
  <div>
    <aw-form v-model="v$" @success="onSuccess">
      <div
        :class="[
          $style.couponForm,
          {
            [$style.couponFormNewline]: isBtnInNewline,
          },
        ]"
      >
        <div :class="$style.couponInput">
          <aw-input-version2
            v-bind="couponCodeValidator.field"
            has-label
            :floating-label-text="$awt('aw.basket_page.add_coupon_code.label')"
          />
          <aw-minimal-error v-bind="couponCodeValidator.error" />
        </div>
        <client-only>
          <aw-form-button
            ref="formButtonElement"
            v-model="v$"
            :aria-label="$awt('aw.basket_page.add_coupon_code.submit_aria')"
            is-aria-used
            shrink
            :styling="screenRange['tablet-min'] ? 'primary-dark' : 'primary-dark-just-icon'"
            :class="$style.submitBtn"
          >
            <span :class="$style.submitBtnContent">
              <span :class="$style.submitBtnText" v-text="$awt('aw.basket_page.add_coupon_code.submit')" />
              <lv-icon :class="$style.submitBtnIcon" name="plus-24" :size="20" />
            </span>
          </aw-form-button>
        </client-only>
      </div>
    </aw-form>
  </div>
</template>

<script setup>
  import { useVuelidate } from '@vuelidate/core';
  import { useId, useNuxtApp } from 'nuxt/app';
  import { awRequired } from '~~/common/utils/form';
  import { PUSH_VOUCHER } from '~~/common/plugins/aw-analytics.js';
  import { useOrderStore } from '~~/common/stores/order';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import AwForm from '~~/common/components/Form/AwForm.vue';
  import AwFormButton from '~~/common/components/Form/AwFormButton.vue';
  import AwInputVersion2, { awInputV2Props } from '~~/common/components/Common/AwInputVersion2.vue';
  import AwMinimalError from '~~/common/components/Common/AwMinimalError.vue';

  defineProps({
    isBtnInNewline: {
      type: Boolean,
      required: true,
      default: true,
    },
  });

  const { $awt, $price, $awAnalytics } = useNuxtApp();

  const [idCouponCode, couponCode] = [useId(), ref('')];

  const v$ = useVuelidate(
    {
      couponCode: {
        required: awRequired(),
      },
    },
    {
      couponCode,
    },
  );

  const formButtonElement = ref(null);

  const couponCodeValidator = computed(() => awInputV2Props({
    validator: v$.value,
    validatorName: 'couponCode',
    model: couponCode,
    id: idCouponCode,
  }));

  const discountsSum = computed(() => {
    const splits = useOrderStore().order?.splits || [];
    const currency = splits[0]?.summary?.discounts?.sum?.currency || '';
    const discounts = splits.reduce(
      (acc, curr) => {
        return acc + (curr?.summary?.discounts?.sum?.gross || 0);
      },
      0,
    );
    return $price({ gross: discounts }, currency);
  });

  const screenRange = computed(() => {
    return useUserInterfaceStore().mediaQueries;
  });

  async function onSuccess () {
    const orderStore = useOrderStore();
    formButtonElement.value.$el.focus();
    const isSuccess = await orderStore.addCoupon({
      id: 'current',
      objectType: 'voucher',
      objectId: couponCode.value,
    });
    if (isSuccess) {
      $awAnalytics[PUSH_VOUCHER]({
        couponCode: couponCode.value,
        discountsSum: discountsSum.value,
      });
    }
    couponCode.value = '';
    v$.value.$reset();
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.couponForm {
  display: flex;
  align-items: start;
  gap: 12px;

  &Newline {
    flex-direction: column;
  }

}

.couponInput {
  display: flex;
  flex: 1;
  flex-direction: column;

  [data-input-block] {
    width: 100%;
  }
}

.submitBtn {
  @include tablet (min) {
    padding: 16px 17px !important;
  }
}

.submitBtnContent {
  display: flex;
  align-items: center;
  justify-content: center;
}

.submitBtnText {
  @include mobile (max) {
    display: none;
  }
}

.submitBtnIcon {
  @include tablet (min) {
    display: none;
  }
}
</style>
